/**
 * @description 各端通用事件
 * @author aprilcai
 */

import EventEmitter, { F } from '../module/event-emitter';
import { invokeAsync, getBridge } from '../module/bridge';
import { throttle, callOnce } from '../module/util';
import { isTenvideoWindows, isTenvideoPad, isTenvideoPhone, isTenvideoWeb } from '../module/ua';
import { resultCode, resultMessage, wrapResult } from '../module/entity';

const eventEmitter = new EventEmitter();

export enum Events {
  networkStateChange = 'networkStateChange',
  pageAppear = 'pageAppear',
  pageDisappear = 'pageDisappear',
  devicemotion = 'devicemotion',
  deviceorientation = 'deviceorientation',
  loginStatusChange = 'loginStatusChange',
}

// 初始化页面可见性改变事件
const initPageVisibilityChange = callOnce(async () => {
  const { pageAppear, pageDisappear } = Events;
  if (isTenvideoWindows) {
    const bridge = await getBridge();
    bridge.$on('onPageShow', () => {
      eventEmitter.emit(pageAppear);
    });
    bridge.$on('onPageHide', () => {
      eventEmitter.emit(pageDisappear);
    });
  } else if (isTenvideoPhone) {
    const bridge = await getBridge();
    bridge.$on('onAppEnterBackground', () => {
      eventEmitter.emit(pageAppear);
    });
    bridge.$on('onAppEnterForeground', () => {
      eventEmitter.emit(pageDisappear);
    });
  } else if (isTenvideoWeb) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        eventEmitter.emit(pageAppear);
      } else {
        eventEmitter.emit(pageDisappear);
      }
    });
  }
});

async function getNetworkState() {
  if (isTenvideoPhone || isTenvideoPad) {
    const res = await invokeAsync('getNetworkState', null);
    const { result } = res;
    return result.state;
  }
  const { navigator } = window;
  const { connection, mozConnection, webkitConnection } = navigator as any;
  const con = connection
    || mozConnection
    || webkitConnection || {
    type: '',
    effectiveType: '',
  };
  let type = '';
  if (con) {
    type = con.type || con.effectiveType || '';
  }
  let state = 1;
  if (type === 'none') {
    state = 0;
  } else if (type === 'cellular') {
    state = 4;
  }
  return state;
}

// 网络状态改变事件
const initNetworkChange = callOnce(async () => {
  const intervalTime = 2000;
  let intervalHandler: any;
  let currentNetworkState: number;
  const get = async () => {
    const networkState = await getNetworkState();
    let newIntervalTime = 0;
    if (currentNetworkState !== networkState) {
      currentNetworkState = networkState;
      eventEmitter.emit(Events.networkStateChange, networkState);
      newIntervalTime = currentNetworkState === 0 ? 1000 : 2000;
      if (newIntervalTime !== intervalTime) {
        intervalGetNetWork();
      }
    }
  };
  const intervalGetNetWork = () => {
    clearInterval(intervalHandler);
    intervalHandler = setInterval(() => {
      get();
    }, intervalTime);
  };
  const state = await getNetworkState();
  currentNetworkState = state;
  intervalGetNetWork();
});

// 登录态切换事件
// web登录切换会刷页面，所以不用初始化
const initOnLoginStatusChange = callOnce(async () => {
  const { loginStatusChange } = Events;
  const codeOk = resultCode.ok;
  const msgOk = resultMessage.ok;
  const bridge = await getBridge();
  if (isTenvideoWindows) {
    bridge.$on('onLogin', () => {
      eventEmitter.emit(
        loginStatusChange,
        wrapResult(codeOk, msgOk, {
          isLogin: true,
        }),
      );
    });
    bridge.$on('onLogout', () => {
      eventEmitter.emit(
        loginStatusChange,
        wrapResult(codeOk, msgOk, {
          isLogin: false,
        }),
      );
    });
  } else if (isTenvideoPhone || isTenvideoPad) {
    bridge.$on('onActionLoginFinish', (res: { errCode: number }) => {
      const { errCode } = res;
      eventEmitter.emit(
        loginStatusChange,
        wrapResult(codeOk, msgOk, {
          isLogin: errCode === 0,
        }),
      );
    });
    bridge.$on('onActionLogoutFinish', () => {
      eventEmitter.emit(
        loginStatusChange,
        wrapResult(codeOk, msgOk, {
          isLogin: false,
        }),
      );
    });
  }
});

const supportRequestPermission = (Event: typeof DeviceOrientationEvent | typeof DeviceMotionEvent) => typeof Event.requestPermission === 'function';
const requestPermission = async (
  Event: typeof DeviceMotionEvent | typeof DeviceOrientationEvent,
): Promise<'granted' | 'denied' | 'prompt'> => {
  // 如果不支持 requestPermission，则默认同意
  if (!supportRequestPermission(Event)) {
    return 'granted';
  }
  const permissionState = await Event.requestPermission();
  console.log('permissionState: ', permissionState);
  return permissionState;
};

// 设备方向变化信息
// https://developer.mozilla.org/zh-CN/docs/Web/API/Detecting_device_orientation
const initOnDeviceorientation = callOnce(async () => {
  const permissionState = await requestPermission(DeviceOrientationEvent);
  const addEvent = () => {
    window.addEventListener(
      'deviceorientation',
      throttle((event: DeviceOrientationEvent) => {
        const { alpha, gamma, beta, absolute } = event;
        eventEmitter.emit(Events.deviceorientation, {
          // 表示设备沿z轴上的旋转角度，范围为0~360
          alpha,
          // 表示设备在x轴上的旋转角度，范围为-180~180。它描述的是设备由前向后旋转的情况。
          gamma,
          // 表示设备在y轴上的旋转角度，范围为-90~90。它描述的是设备由左向右旋转的情况。
          beta,
          // 表示该设备是否提供绝对定位数据 (这个数据是关于地球的坐标系) 或者使用了由设备决定的专门的坐标系
          // 如果方向数据跟地球坐标系和设备坐标系有差异，则absolute为true，
          // 如果方向数据由设备本身的坐标系提供，则absolute为false。
          absolute,
        });
      }, 100),
      true,
    );
  };

  if (permissionState === 'granted') {
    addEvent();
    return;
  }

  // iOS 下需要在用户点击的周期内触发授权操作才能正常授权
  // https://developer.apple.com/documentation/webkitjs/deviceorientationevent
  const onBodyTouch = async () => {
    const permissionState = await requestPermission(DeviceOrientationEvent);
    if (permissionState === 'granted') {
      addEvent();
    }
    document.body.removeEventListener('touchstart', onBodyTouch);
  };
  document.body.addEventListener('touchstart', onBodyTouch);
});

// 设备的位置和方向改变的速度的信息
// https://developer.mozilla.org/zh-CN/docs/Web/API/Detecting_device_orientation
const initOnDevicemotion = callOnce(async () => {
  const permissionState = await requestPermission(DeviceMotionEvent);
  const addEvent = () => {
    window.addEventListener(
      'devicemotion',
      throttle((event: DeviceMotionEvent) => {
        const { acceleration, accelerationIncludingGravity, rotationRate, interval } = event;
        eventEmitter.emit(Events.devicemotion, {
          // 设备在X,Y,Z轴方向上加速度的对象。加速度的单位为 m/s2
          acceleration,
          // 设备在X,Y,Z轴方向上带重力的加速度的对象。加速度的单位为 m/s2
          accelerationIncludingGravity,
          // 设备在 alpha，beta， gamma轴方向上旋转的速率的对象。旋转速率的单位为 度/s
          rotationRate,
          // 设备获取数据的频率，单位是毫秒。
          interval,
        });
      }, 100),
      true,
    );
  };

  if (permissionState === 'granted') {
    addEvent();
    return;
  }

  // iOS 下需要在用户点击的周期内触发授权操作才能正常授权
  // https://developer.apple.com/documentation/webkitjs/deviceorientationevent
  const onBodyTouch = async () => {
    const permissionState = await requestPermission(DeviceMotionEvent);
    if (permissionState === 'granted') {
      addEvent();
    }
    document.body.removeEventListener('touchstart', onBodyTouch);
  };
  document.body.addEventListener('touchstart', onBodyTouch);
});

function initEvents(e: Events) {
  switch (e) {
    case Events.pageAppear:
    case Events.pageDisappear:
      initPageVisibilityChange();
      break;
    case Events.networkStateChange:
      initNetworkChange();
      break;
    case Events.devicemotion:
      initOnDevicemotion();
      break;
    case Events.deviceorientation:
      initOnDeviceorientation();
      break;
    case Events.loginStatusChange:
      initOnLoginStatusChange();
      break;
  }
}

export function $on(e: Events, fn: F) {
  initEvents(e);
  eventEmitter.on(e, fn);
}

export function $once(e: Events, fn: F) {
  initEvents(e);
  eventEmitter.once(e, fn);
}

export function $off(e: Events, fn: F) {
  eventEmitter.off(e, fn);
}
